import { Fragment } from "react";
import type { SharedDealFields } from "~/utils/deal";
import { Box } from "../Box/Box";
import { Button } from "../Button/Button";
import type { ModalWidth } from "../Modal/Modal";
import { ModalDrawer } from "../ModalDrawer/ModalDrawer";
import { Stack } from "../Stack/Stack";
import { Text } from "../Text/Text";

// Renders deal titles inline in bold, joined grammatically (e.g. "A", "A and B", "A, B and C")
function renderDealNames(deals: SharedDealFields[]): React.ReactNode {
  return deals.map((deal, index) => (
    <Fragment key={deal.id}>
      {index > 0 && (index === deals.length - 1 ? " and " : ", ")}
      <Text inline weight="strong">
        {deal.title}
      </Text>
    </Fragment>
  ));
}

// Explains the conflict, tailored to which deal (new or existing) is exclusive
function ConflictMessage(props: {
  newDealTitle: string | undefined;
  newDealExclusive: boolean;
  dealsToRemove: SharedDealFields[];
}): React.ReactNode {
  const { newDealTitle, newDealExclusive, dealsToRemove } = props;

  // Fallback message, shouldn't normally happen
  if (!newDealTitle || dealsToRemove.length === 0) {
    return (
      <Text>
        Exclusive deals can't be combined with other deals. Choose which deal
        you'd like to use.
      </Text>
    );
  }

  const dealsToRemoveNames = renderDealNames(dealsToRemove);

  if (newDealExclusive) {
    return (
      <Text>
        <Text inline weight="strong">
          {newDealTitle}
        </Text>{" "}
        is an exclusive deal and can't be combined with other deals. Adding it
        will remove {dealsToRemoveNames} from your cart.
      </Text>
    );
  }

  const isMultiple = dealsToRemove.length > 1;

  return (
    <Text>
      {dealsToRemoveNames}{" "}
      {isMultiple ? "are exclusive deals" : "is an exclusive deal"} and can't be
      combined with other deals. Adding{" "}
      <Text inline weight="strong">
        {newDealTitle}
      </Text>{" "}
      will remove {isMultiple ? "them" : "it"} from your cart.
    </Text>
  );
}

function ConflictActions(props: {
  onReplace: () => void;
  onKeepCurrent: () => void;
}): React.ReactNode {
  return (
    <Stack space="2">
      <Button primary onClick={props.onReplace}>
        Remove existing & add new deal
      </Button>
      {/* Grey fill behind the transparent secondary button so it stays distinct
          from the beige surface while keeping its hover/active states. */}
      <Box background="neutral-300" borderRadius="standard">
        <Button onClick={props.onKeepCurrent}>Keep my current cart</Button>
      </Box>
    </Stack>
  );
}

interface ExclusiveDealConflictProps {
  open: boolean;
  newDealTitle: string | undefined;
  newDealExclusive: boolean;
  dealsToRemove: SharedDealFields[];
  onReplace: () => void;
  onKeepCurrent: () => void;
  modalWidth?: ModalWidth;
}

// Confirmation shown when adding a deal would break the rule that
// an exclusive deal must be the only deal in the cart.
export function ExclusiveDealConflictDialog(
  props: ExclusiveDealConflictProps,
): React.ReactNode {
  const {
    open,
    newDealTitle,
    newDealExclusive,
    dealsToRemove,
    onKeepCurrent,
    onReplace,
    modalWidth,
  } = props;

  return (
    <ModalDrawer
      open={open}
      title="Exclusive Deal Limit"
      onClose={onKeepCurrent}
      background="jets-beige"
      modalWidth={modalWidth}
      footer={
        <ConflictActions onReplace={onReplace} onKeepCurrent={onKeepCurrent} />
      }
    >
      <ConflictMessage
        newDealTitle={newDealTitle}
        newDealExclusive={newDealExclusive}
        dealsToRemove={dealsToRemove}
      />
    </ModalDrawer>
  );
}
